import type React from "react";
import { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  List,
  ListItem,
  Typography,
  TextField,
  IconButton,
  Skeleton
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { uniqueId } from "lodash-es";
import { useSchedulerStore } from "../../state/scheduler.store";
import { initializeOliverChat, usersResponse } from "./messageService";

interface ChatBoxProps {
  view: string;
  showChatIcon?: boolean;
}

interface ChatMessages {
  id: string;
  role: string;
  content: string;
}

export const ChatBox: React.FC<ChatBoxProps> = ({ view, showChatIcon }) => {
  const [messageBoxChats, setMessageBoxChats] = useState<ChatMessages[]>([]);
  const [textBoxValue, setTextBoxValue] = useState<string>("");
  const [awaitingAIResponse, setAwaitingAIResponse] = useState<boolean>(false);
  const [disableChat, setDisableChat] = useState<boolean>(false);

  const location = document.location;
  let oliverUrl = "";
  const chatBoxRef = useRef<HTMLUListElement>(null);

  // Adjust the height calculation for small screens
  let messageBoxHeight = "100vh";
  if (showChatIcon) {
    messageBoxHeight = "calc(100% - 60px)"; // Account for the input box at the bottom
  } else if (view === "monthView") {
    messageBoxHeight = "480px";
  } else if (view === "meetingConfirmationView") {
    messageBoxHeight = "480px";
  } else if (view === "columnView" || view === "weeklyView") {
    messageBoxHeight = "calc(90vh - 60px)";
  } else if (view === "availability") {
    messageBoxHeight = "calc(90vh - 20px)";
  } else if (view === "availability-review") {
    messageBoxHeight = "400px";
  } else {
    messageBoxHeight = "calc(90vh - 20px)";
  }

  if (location.hostname === "localhost") {
    oliverUrl = "http://localhost:4000/api/";
  } else {
    oliverUrl =
      "https://us-central1-rooster-staging.cloudfunctions.net/oliver/api/";
  }

  const {
    meetingLink,
    // host,
    sessionData,
    // actions,
    // clientId,
    ui,
    // clientLogo,
    loadingAvailability
  } = useSchedulerStore();

  useEffect(() => {
    if (ui?.currentStep !== "loading") {
      // check session storage if there are any previous messages
      const prevMessages = sessionStorage.getItem("oliverMessages");
      // if so, set them to the state
      if (prevMessages) {
        setMessageBoxChats(JSON.parse(prevMessages));
        setDisableChat(false);
        return;
      }

      // if not, send request to initialize api
      const data = {
        availabilityID: meetingLink?.id,
        requestID: "sqdsjlk",
        timezone: sessionData?.selectedTimezone
      };

      const initializeOliver = async () => {
        try {
          setDisableChat(true);
          const msg = await initializeOliverChat(data, oliverUrl);
          if (msg.length > 0) {
            setMessageBoxChats(msg);
            // set the messages to session storage
            sessionStorage.setItem("oliverMessages", JSON.stringify(msg));
            setDisableChat(false);
          } else {
            setMessageBoxChats([
              {
                id: uniqueId(),
                role: "assistant",
                content: "Oliver is busy processing other requests"
              }
            ]);
            setDisableChat(true);
          }
        } catch (error) {
          console.error(`error: ${error}`);
          setDisableChat(true);
        }
      };

      initializeOliver().then(() => {
        sessionStorage.setItem("chatLoaded", "true");
        return;
      });
    }

    if (sessionStorage.getItem("chatLoaded") === "true") {
      // check session storage if there are any previous messages
      const prevMessages = sessionStorage.getItem("oliverMessages");
      if (prevMessages) {
        // if so, set them to the state
        setMessageBoxChats(JSON.parse(prevMessages));
        setDisableChat(false);
        return;
      }
    }
  }, [loadingAvailability]);

  useEffect(() => {
    scrollChatToBottom();
  }, [messageBoxChats]);

  const pushToSessionStorage = (data: ChatMessages) => {
    const prevMessages = sessionStorage.getItem("oliverMessages");
    const updatedMessages = prevMessages ? JSON.parse(prevMessages) : [];
    updatedMessages.push(data);
    sessionStorage.setItem("oliverMessages", JSON.stringify(updatedMessages));
  };

  const handleUserResponse = async (userResponse: string) => {
    try {
      const reconstructedData = {
        id: uniqueId(),
        role: "user",
        content: userResponse
      };

      setMessageBoxChats((prevValue) => {
        const newValue = [...prevValue, reconstructedData];
        return newValue;
      });
      setAwaitingAIResponse(true);

      // append to the session storage
      pushToSessionStorage(reconstructedData);

      const { messages, availableSlots } = await usersResponse(
        {
          userResponse,
          availabilityID: meetingLink?.id,
          requestID: "asdh",
          timezone: sessionData?.selectedTimezone
        },
        oliverUrl
      );

      // console.log(availableSlots);

      setMessageBoxChats((prevValue) => {
        const newValue = [...prevValue, messages];
        return newValue;
      });
      setAwaitingAIResponse(false);

      // append to the session storage
      pushToSessionStorage(messages);
    } catch (error) {
      console.log(`error: ${JSON.stringify(error)}`);
    }
  };

  const scrollChatToBottom = () => {
    const listElement =
      document.getElementById("chatBox") || chatBoxRef.current;
    const hasScrolledToBottom = (element) => {
      if (!element) return true;
      return (
        Math.abs(
          element.scrollHeight - element.clientHeight - element.scrollTop
        ) <= 1
      );
    };

    if (listElement) {
      while (!hasScrolledToBottom(listElement)) {
        listElement.scrollTop = listElement.scrollHeight * 1.5;
      }
    }
  };

  if (ui?.currentStep === "loading") {
    return <></>;
  }

  return (
    <>
      <List
        id="chatBox"
        ref={chatBoxRef}
        sx={{
          height: messageBoxHeight,
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none"
          },
          p: 0
        }}
      >
        {messageBoxChats.map((msg) => (
          <ListItem
            key={msg.id}
            sx={{
              justifyContent:
                msg.role === "assistant" ? "flex-start" : "flex-end"
            }}
          >
            <Paper
              elevation={1}
              sx={{
                padding: "10px",
                borderRadius: "10px",
                width: "fit-content",
                maxWidth: "60%"
              }}
            >
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontSize: {
                    xs: "0.75rem",
                    sm: "0.875rem",
                    md: "0.7rem",
                    lg: "0.8rem",
                    xl: "0.8rem"
                  },
                  display: "flex",
                  justifyContent:
                    msg.role === "assistant" ? "flex-start" : "flex-end"
                }}
              >
                {msg.content}
              </Typography>
            </Paper>
          </ListItem>
        ))}

        {awaitingAIResponse && (
          <Skeleton
            variant="rectangular"
            width="60%"
            height={60}
            sx={{ borderRadius: "10px", marginTop: "10px", ml: 2 }}
          />
        )}
      </List>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "10px"
        }}
        component={"form"}
        onSubmit={(e) => {
          e.preventDefault();
          handleUserResponse(textBoxValue);
          setTextBoxValue("");
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Type a message"
          fullWidth
          size="small"
          value={textBoxValue}
          onChange={(e) => setTextBoxValue(e.target.value)}
          disabled={disableChat}
          InputProps={{
            endAdornment: (
              <IconButton
                component="button"
                aria-label="Send message"
                sx={{ minWidth: "auto", pl: 0, pr: 0 }}
                onClick={() => {
                  handleUserResponse(textBoxValue);
                  setTextBoxValue("");
                }}
                disabled={disableChat}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            )
          }}
          sx={{ marginRight: "10px" }}
        />
      </Box>
    </>
  );
};
